<template>
  <div class="lang-selector" data-ui-copy="preserve">
    <button
      ref="trigger"
      class="lang-selector__trigger"
      type="button"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      :title="currentLanguageName"
      @click="toggleDropdown"
    >
      <Icon name="Globe" :size="16" />
      <span class="lang-selector__label">{{ currentLanguageLabel }}</span>
      <Icon name="ChevronDown" :size="14" class="lang-selector__caret" :class="{ 'is-rotated': isOpen }" />
    </button>

    <transition name="lang-fade">
      <div v-if="isOpen" class="lang-selector__menu" role="menu" @click.stop>
        <button
          v-for="lang in supportedLanguages"
          :key="lang.code"
          type="button"
          role="menuitemradio"
          :aria-checked="currentLanguage === lang.code"
          class="lang-selector__item"
          :class="{ 'is-active': currentLanguage === lang.code }"
          @click="handleLanguageChange(lang.code)"
        >
          <span class="lang-selector__native">{{ lang.nativeName }}</span>
          <Icon
            v-if="currentLanguage === lang.code"
            name="Check"
            :size="14"
            class="lang-selector__check"
          />
        </button>
      </div>
    </transition>

    <div v-if="isOpen" class="lang-selector__backdrop" @click="isOpen = false"></div>
  </div>
</template>

<script>
import logger from '@/services/logger';
import { getSupportedLanguages, getCurrentLanguage, changeLanguage } from '@/i18n';
import Icon from '@/shared/ui/Icon.vue';

export default {
  name: 'LanguageSelector',
  components: { Icon },
  data() {
    return {
      currentLanguage: getCurrentLanguage(),
      supportedLanguages: getSupportedLanguages(),
      isOpen: false,
    };
  },
  computed: {
    currentLanguageName() {
      const lang = this.supportedLanguages.find(l => l.code === this.currentLanguage);
      return lang ? lang.nativeName : 'English';
    },
    currentLanguageLabel() {
      // Show ISO-like short code in the trigger for compactness.
      return (this.currentLanguage || 'en').toUpperCase().slice(0, 2);
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    toggleDropdown() { this.isOpen = !this.isOpen; },
    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.isOpen = false;
        this.$refs.trigger.focus();
      }
    },
    handleLanguageChange(langCode) {
      if (this.currentLanguage !== langCode) {
        changeLanguage(langCode);
        this.currentLanguage = langCode;
        this.isOpen = false;
        logger.log('LanguageSelector', `Language changed to: ${langCode}`);
      } else {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style scoped>
.lang-selector { position: relative; display: inline-flex; }
.lang-selector__trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  gap: 7px;
  padding: 0 9px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: transparent;
  color: var(--workspace-accent);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: background .18s ease, border-color .18s ease;
}
.lang-selector__trigger:hover, .lang-selector__trigger[aria-expanded="true"] {
  background: var(--workspace-accent-surface);
  border-color: var(--workspace-accent-border);
}
.lang-selector__label { font-family: inherit; font-size: 12px; letter-spacing: .04em; }
.lang-selector__caret { opacity: .6; transition: transform .18s ease; }
.lang-selector__caret.is-rotated { transform: rotate(180deg); }
.lang-selector__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 140px;
  padding: 5px;
  border: 1px solid var(--workspace-line);
  border-radius: 7px;
  background: var(--surface);
  box-shadow: 0 8px 24px rgba(38,55,70,.09);
  z-index: var(--z-dropdown);
}
.lang-selector__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 38px;
  padding: 0 11px;
  border: 0;
  border-radius: 3px;
  background: transparent;
  color: var(--workspace-ink);
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background .15s ease;
}
.lang-selector__item + .lang-selector__item { margin-top: 3px; }
.lang-selector__item:hover { background: var(--workspace-accent-surface); }
.lang-selector__item.is-active { background: var(--workspace-research-surface); color: var(--workspace-research); }
.lang-selector__native { font-weight: 500; }
.lang-selector__check { flex: 0 0 auto; color: var(--workspace-research); }
.lang-selector__trigger:focus-visible, .lang-selector__item:focus-visible { outline: 2px solid var(--workspace-accent); outline-offset: 2px; }
.lang-selector__backdrop { position: fixed; inset: 0; background: transparent; z-index: calc(var(--z-dropdown) - 1); }
.lang-fade-enter-active, .lang-fade-leave-active { transition: opacity .15s ease, transform .15s ease; }
.lang-fade-enter-from, .lang-fade-leave-to { opacity: 0; transform: translateY(-4px); }
@media (max-width: 768px) { .lang-selector__label { display: none; }.lang-selector__trigger { padding-inline: 6px; gap: 4px; } }
@media (prefers-reduced-motion: reduce) { .lang-fade-enter-active, .lang-fade-leave-active { transition: none; } }
</style>
